import type { SupabaseClient } from "@supabase/supabase-js";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { logAuth } from "../auth/debug-log";
import { fetchUserProjects } from "./fetch";
import type { Project } from "./schema";

// Clé localStorage du projet courant — source de vérité unique partagée par tout
// l'app (Accueil, Captures, Importer, Personnes). Historiquement posée par Home ;
// on garde la même clé pour la rétro-compat (et le repli à froid qui la lit).
export const SELECTED_PROJECT_KEY = "captureBesoins.selectedProjectId";

// Projet par défaut quand aucune sélection valide n'est persistée. Tricoté.es
// (projet partagé fondateur) sinon le premier projet accessible.
const DEFAULT_PROJECT_SLUG = "tricotees";

/** Compte dont on veut les projets.
 *  `undefined` = on ne sait pas encore (session en cours de résolution) ;
 *  `null` = personne n'est connecté ; une chaîne = l'id du compte connecté. */
export type CompteCourant = string | null | undefined;

// Tout compte connecté possède au minimum son projet personnel. Une liste vide
// n'est donc jamais une vérité pour un compte connecté : c'est une lecture non
// authentifiée ou filtrée qui a répondu « 200 [] ». On la traite comme un échec.
export const ERREUR_LISTE_VIDE = "Projets indisponibles pour ce compte";

// Reprise à froid (retour terrain du 25 août : « Projets non chargés » au PREMIER
// chargement sur iPhone). Sur un téléphone qui démarre, la pile réseau n'est pas
// toujours prête quand le webview boote, et une lecture dont le jeton n'est pas
// encore attaché répond « 200 [] ». Afficher l'erreur au premier échec, c'est
// accuser l'app d'un problème qu'elle n'a pas encore — et c'est la première
// chose que voit l'usager. On réessaie avant de parler.
//
// Trois tentatives, ~1,6 s au pire avant de se taire et d'afficher l'erreur :
// invisible quand ça repasse, honnête quand ça ne repasse pas.
const ATTENTES_MS = [400, 1200];

const patienter = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Tentative = { ok: true; data: Project[] } | { ok: false; motif: string };

// Une lecture, avec « 200 [] » normalisé en ÉCHEC NOMMÉ : tout compte connecté
// possède au moins son projet personnel, donc une liste vide n'est jamais une
// vérité pour lui — c'est une lecture non authentifiée ou filtrée. Sortir cette
// normalisation du composant laisse à `charger` une seule responsabilité.
async function lireUneFois(client: SupabaseClient): Promise<Tentative> {
  const r = await fetchUserProjects(client);
  if (!r.ok) return { ok: false, motif: r.error };
  if (r.data.length === 0) return { ok: false, motif: ERREUR_LISTE_VIDE };
  return { ok: true, data: r.data };
}

// Lit avec reprises, et rend le résultat FINAL : les données, ou le motif du
// dernier échec. `null` veut dire « le composant a disparu entre deux
// tentatives » — on ne réveille pas un écran qui n'existe plus.
async function lireAvecReprises(
  client: SupabaseClient,
  vivant: () => boolean,
): Promise<Tentative | null> {
  let motif = "";
  for (let essai = 0; essai <= ATTENTES_MS.length; essai++) {
    const t = await lireUneFois(client);
    if (!vivant()) return null;
    if (t.ok) {
      if (essai > 0) logAuth("info", `projets chargés au ${essai + 1}ᵉ essai`);
      return t;
    }
    motif = t.motif;
    logAuth("warn", `chargement des projets raté (essai ${essai + 1})`, motif);
    const attente = ATTENTES_MS[essai];
    if (attente === undefined) break;
    await patienter(attente);
    if (!vivant()) return null;
  }
  logAuth("warn", "chargement des projets abandonné", motif);
  return { ok: false, motif };
}

// Résout le projet courant à partir des projets accessibles et de l'id persisté.
// Pur (testable hors React) : id persisté valide → lui ; sinon Tricoté.es ;
// sinon premier projet ; sinon null (aucun projet accessible).
export function resolveCurrentProjectId(
  projects: Project[],
  persistedId: string | null,
): string | null {
  if (persistedId && projects.some((p) => p.id === persistedId)) return persistedId;
  const fallback = projects.find((p) => p.slug === DEFAULT_PROJECT_SLUG) ?? projects[0];
  return fallback?.id ?? null;
}

export interface CurrentProjectState {
  projects: Project[];
  currentProjectId: string | null;
  currentProject: Project | null;
  setCurrentProject: (projectId: string) => void;
  loading: boolean;
  /** Message d'échec du dernier chargement, null si tout va bien. Une liste
   *  vide n'est PAS une vérité tant que ceci n'est pas null : l'accueil doit
   *  distinguer « aucun projet » de « on n'a pas réussi à les charger ». */
  erreur: string | null;
  /** Relance le chargement (bouton « Réessayer »). */
  rechargerProjets: () => Promise<void>;
}

const ProjectContext = createContext<CurrentProjectState | null>(null);

interface ProjectProviderProps {
  client: SupabaseClient;
  /** Le compte connecté. Sans lui, on ne charge rien : interroger `projects`
   *  sans session renvoie « 200 [] », que l'app prenait pour « aucun projet ». */
  compte: CompteCourant;
  children: ReactNode;
}

export function ProjectProvider({ client, compte, children }: ProjectProviderProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_PROJECT_KEY),
  );
  const vivant = useRef(true);

  const appliquer = useCallback((data: Project[]) => {
    setErreur(null);
    setProjects(data);
    const resolved = resolveCurrentProjectId(data, localStorage.getItem(SELECTED_PROJECT_KEY));
    setCurrentProjectId(resolved);
    if (resolved) localStorage.setItem(SELECTED_PROJECT_KEY, resolved);
    setLoading(false);
  }, []);

  const charger = useCallback(async () => {
    setLoading(true);
    const t = await lireAvecReprises(client, () => vivant.current);
    if (t === null) return;
    if (t.ok) {
      appliquer(t.data);
      return;
    }
    // On conserve la liste et l'id persisté : afficher un vide muet, c'est ce
    // qui a fait perdre 40 minutes d'enregistrement le 17 août. Mais on nomme
    // la cause plutôt que de la taire.
    setErreur(t.motif);
    setLoading(false);
  }, [client, appliquer]);

  // Vide ce qui appartenait au compte qui vient de partir : sans ça, l'id du
  // projet précédent survit à la déconnexion et sert de cible d'écriture au
  // compte suivant, qui n'y a pas droit.
  const oublier = useCallback(() => {
    localStorage.removeItem(SELECTED_PROJECT_KEY);
    setProjects([]);
    setCurrentProjectId(null);
    setErreur(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    vivant.current = true;
    if (compte === undefined) {
      // Session encore inconnue : on ne charge rien et on reste en attente.
      setLoading(true);
    } else if (compte === null) {
      oublier();
    } else {
      void charger();
    }
    return () => {
      vivant.current = false;
    };
  }, [compte, charger, oublier]);

  const recharger = useCallback(async () => {
    if (typeof compte === "string") await charger();
  }, [compte, charger]);

  const setCurrentProject = useCallback((projectId: string) => {
    setCurrentProjectId(projectId);
    localStorage.setItem(SELECTED_PROJECT_KEY, projectId);
  }, []);

  const value = useMemo<CurrentProjectState>(
    () => ({
      projects,
      currentProjectId,
      currentProject: projects.find((p) => p.id === currentProjectId) ?? null,
      setCurrentProject,
      loading,
      erreur,
      rechargerProjets: recharger,
    }),
    [projects, currentProjectId, setCurrentProject, loading, erreur, recharger],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useCurrentProject(): CurrentProjectState {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error("useCurrentProject doit être utilisé à l'intérieur d'un ProjectProvider");
  }
  return ctx;
}
